import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { SectionHeading } from "@/components/ui-ext/SectionHeading";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { PORTFOLIO } from "@/content/portfolio";

export const Route = createFileRoute("/results")({
  head: () => ({
    ...pageMeta({
    path: "/results/",
    title: "SEO Results Dashboard — Live Traffic & Ranking Data | Optimize Plus",
    description:
      "Aggregate SEO performance across every Optimize Plus engagement — sourced live from Semrush and Google Search Console with dated screenshots, keyword counts, and traffic movement.",
    keywords: [
      "SEO results",
      "SEO dashboard",
      "SEO performance data",
      "Semrush results",
      "Search Console results",
      "agency SEO proof",
    ],
    }),
    scripts: ldScripts(
      breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "Results", path: "/results/" },
      ]),
      faqLd([
        {
          q: "Where does the data on this page come from?",
          a: "Every figure is pulled from a first-party source captured during the engagement window — Google Search Console, Google Analytics 4, Semrush, or BrightLocal for local visibility. We do not model, estimate, or extrapolate numbers we have not directly measured.",
        },
        {
          q: "Why do different clients report different metrics?",
          a: "Because the metric that matters depends on the business model. A lead-generation service business cares about qualified calls and form fills; an ecommerce brand cares about organic revenue; a multi-location business cares about map-pack visibility per location. We report what is actually decision-useful for each client rather than forcing every engagement into one template.",
        },
        {
          q: "Can SEO results really be attributed cleanly to your work?",
          a: "Not perfectly, and we say so. Search algorithms change independently of our work, competitors move, seasonality shifts demand, and a client's own site changes can help or hurt in the same window we are active. We control for this by tracking movement against a pre-engagement baseline and by flagging any known confounding factor — a site redesign, a Google core update, a change in ad spend — in the same reporting period it occurred.",
        },
        {
          q: "How do you measure AI-search citations?",
          a: "We track whether client pages are cited or referenced in AI Overviews, ChatGPT, and Perplexity responses for a defined set of target queries, checked on a recurring cadence using Semrush's AI Search tracking alongside manual spot checks. This is a newer and noisier measurement than classic rankings, and we treat it as directional evidence rather than a precise, stable number.",
        },
        {
          q: "How often do clients receive reports?",
          a: "Standard cadence is monthly, with a live dashboard available in between for clients who want to check in more often. Larger or more complex engagements sometimes move to a fortnightly cadence during active technical work, then settle back to monthly once the programme stabilises.",
        },
        {
          q: "What should I be sceptical of when reading someone else's case study?",
          a: "Watch for percentage gains with no baseline shown, screenshots with no visible date range, traffic charts that blend branded and non-branded search, and headline numbers that don't disclose the engagement length. A credible case study names the source, shows the date window, and is specific about what changed and why.",
        },
        {
          q: "Do you ever show results that didn't work?",
          a: "We show what we can source and verify. Not every tactic inside an engagement moves the needle, and we discuss those internally with clients as part of ongoing reporting — but this results page aggregates headline outcomes across live engagements, not a full audit trail of every experiment we've run.",
        },
      ]),
    ),
  }),
  component: ResultsPage,
});

const AGG = [
  { label: "Monthly organic visits", value: "8.0K+", sub: "Across active engagements", tone: "primary" },
  { label: "Ranking keywords", value: "9.6K+", sub: "Combined portfolio" },
  { label: "Referring domains", value: "1,120+", sub: "Earned via digital PR" },
  { label: "GSC impressions / 6 mo", value: "680K+", sub: "Lynx + HVAC combined" },
  { label: "AI-cited pages", value: "137", sub: "Semrush AI Search · Carage Auto" },
  { label: "Avg. click growth", value: "+47%", sub: "Weighted across engagements" },
];

type SortKey = "client" | "industry" | "window" | "result";

function ResultsPage() {
  const [sortKey, setSortKey] = useState<SortKey>("client");
  const [asc, setAsc] = useState(true);

  const rows = useMemo(() => {
    const sorted = [...PORTFOLIO].sort((a, b) => {
      const va =
        sortKey === "client"
          ? a.client
          : sortKey === "industry"
            ? a.industry
            : sortKey === "window"
              ? a.window
              : a.metrics[0]?.value ?? "";
      const vb =
        sortKey === "client"
          ? b.client
          : sortKey === "industry"
            ? b.industry
            : sortKey === "window"
              ? b.window
              : b.metrics[0]?.value ?? "";
      return asc ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return sorted;
  }, [sortKey, asc]);

  const toggle = (k: SortKey) => {
    if (k === sortKey) setAsc(!asc);
    else {
      setSortKey(k);
      setAsc(true);
    }
  };

  // Simple normalized bar chart by "keyword count" using metric labels heuristically
  const chartRows = PORTFOLIO.map((p) => {
    const kw = p.metrics.find((m) => /keyword|clicks|traffic|impress/i.test(m.label));
    const raw = kw?.value ?? p.metrics[0].value;
    const num = parseFloat(raw.replace(/[^\d.]/g, "")) || 0;
    const scale = /K/i.test(raw) ? 1000 : /M/i.test(raw) ? 1_000_000 : 1;
    return { client: p.client, slug: p.slug, label: kw?.label ?? p.metrics[0].label, raw, num: num * scale };
  });
  const max = Math.max(...chartRows.map((r) => r.num));

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-20 md:pt-28 pb-10">
          <SectionHeading
            as="h1"
            eyebrow="Results dashboard"
            title="The numbers behind our proven SEO growth process."
            intro="Every metric on this page is aggregated from real engagements documented in our case studies. Each row links to its sourced Semrush or Search Console screenshot."
          />
        </section>

        {/* Aggregate stat grid */}
        <section className="container-page pb-12">
          <div className="grid gap-4 md:grid-cols-3">
            {AGG.map((s) => (
              <div key={s.label} className="surface-card p-6 relative overflow-hidden">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  {s.label}
                </div>
                <div
                  className={`mt-3 font-display text-5xl ${
                    s.tone === "primary" ? "text-gradient" : "text-foreground"
                  }`}
                >
                  {s.value}
                </div>
                <div className="mt-3 text-sm text-muted-foreground">{s.sub}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Distribution / bar chart */}
        <section className="container-page py-12">
          <EyebrowLabel>Portfolio distribution</EyebrowLabel>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Headline metric by engagement
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Normalized comparison across the primary tracked metric for each
            engagement. Different clients have different KPIs — traffic,
            keywords, impressions, clicks — so labels are shown per row.
          </p>
          <div className="mt-8 surface-card p-6 md:p-8">
            <ul className="space-y-5">
              {chartRows.map((r) => (
                <li key={r.slug}>
                  <div className="flex items-baseline justify-between text-sm">
                    <Link
                      to="/portfolio/$slug/"
                      params={{ slug: r.slug }}
                      className="text-foreground hover:text-primary"
                    >
                      {r.client}
                    </Link>
                    <span className="text-muted-foreground">
                      <span className="text-primary font-medium">{r.raw}</span>{" "}
                      <span className="text-[0.7rem] uppercase tracking-wider">
                        {r.label}
                      </span>
                    </span>
                  </div>
                  <div className="mt-2 h-2 w-full bg-surface-2 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary/60 to-primary"
                      style={{ width: `${max ? (r.num / max) * 100 : 0}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Sortable engagement table */}
        <section className="container-page py-12">
          <EyebrowLabel>By engagement</EyebrowLabel>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Every client. Every window.
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Click a column header to sort. Click a row for the full breakdown.
          </p>
          <div className="mt-8 overflow-x-auto surface-card">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-left text-[0.7rem] uppercase tracking-widest text-muted-foreground">
                <tr>
                  {(
                    [
                      ["client", "Client"],
                      ["industry", "Industry"],
                      ["window", "Window"],
                      ["result", "Headline result"],
                    ] as [SortKey, string][]
                  ).map(([k, l]) => (
                    <th
                      key={k}
                      className="p-4 cursor-pointer select-none hover:text-primary"
                      onClick={() => toggle(k)}
                    >
                      {l}{" "}
                      <span className="opacity-60">
                        {sortKey === k ? (asc ? "▲" : "▼") : "↕"}
                      </span>
                    </th>
                  ))}
                  <th className="p-4">Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const primary = p.metrics[0];
                  return (
                    <tr
                      key={p.slug}
                      className="border-t border-border hover:bg-surface-2 transition-colors"
                    >
                      <td className="p-4">
                        <Link
                          to="/portfolio/$slug/"
                          params={{ slug: p.slug }}
                          className="text-foreground hover:text-primary"
                        >
                          {p.client}
                        </Link>
                        <div className="text-[0.7rem] text-muted-foreground mt-0.5">
                          {p.domain}
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground">{p.industry}</td>
                      <td className="p-4 text-muted-foreground">{p.window}</td>
                      <td className="p-4">
                        <span className="text-primary font-medium">
                          {primary.value}
                        </span>{" "}
                        <span className="text-muted-foreground">
                          {primary.label}
                        </span>
                      </td>
                      <td className="p-4 text-muted-foreground text-xs">
                        {primary.source}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Methodology / trust */}
        <section className="container-page py-16">
          <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
            <div>
              <EyebrowLabel>Methodology</EyebrowLabel>
              <h2 className="mt-3 font-display text-3xl md:text-4xl">
                How we count.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Every number on this page is captured from a first-party source
                — either the client's Google Search Console or a Semrush pull
                inside the engagement window. We don't estimate. We don't blend
                paid into organic. We don't quote lifetime numbers when only the
                last six months matter.
              </p>
            </div>
            <ul className="grid gap-4 sm:grid-cols-2">
              {[
                {
                  h: "Sourced",
                  b: "Every metric names its source (GSC, Semrush, GA4, BrightLocal).",
                },
                {
                  h: "Dated",
                  b: "Every metric shows the capture date or engagement window.",
                },
                {
                  h: "Named",
                  b: "Every metric ties to a named client with a live domain.",
                },
                {
                  h: "Reversible",
                  b: "We hand you the reporting stack. Cancel any time; keep the assets.",
                },
              ].map((x) => (
                <li key={x.h} className="surface-card p-5">
                  <div className="text-primary text-xs uppercase tracking-widest">
                    {x.h}
                  </div>
                  <div className="mt-2 text-sm text-foreground/90">{x.b}</div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA */}
        <section className="container-page py-16">
          <div className="surface-card p-8 md:p-12 grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <EyebrowLabel>Your dashboard, next</EyebrowLabel>
              <h2 className="mt-3 font-display text-3xl md:text-4xl">
                See what your numbers could look like.
              </h2>
              <p className="mt-3 text-muted-foreground max-w-xl">
                Book a free strategy call. We'll audit your organic footprint,
                benchmark you against these engagements, and tell you honestly
                whether we're the right fit.
              </p>
            </div>
            <Link
              to="/contact/"
              search={{ location: "", service: "" }}
              className="inline-flex whitespace-nowrap rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
            >
              Book a strategy call →
            </Link>
          </div>
        </section>

        {/* Editorial: methodology deep dive */}
        <section className="container-page py-16 max-w-3xl">
          <EyebrowLabel>How to read this page</EyebrowLabel>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Measurement methodology, in detail.
          </h2>
          <div className="mt-6 space-y-5 text-sm leading-relaxed text-foreground/85">
            <p>
              Every engagement we run starts by capturing a baseline — organic traffic, ranking keyword count, referring domains, and (where relevant) AI-search citation frequency — in the thirty days before any work begins. That baseline is the fixed point everything else is measured against. Without it, "traffic went up" is meaningless, because traffic moves for a dozen reasons unrelated to the work we do.
            </p>
            <p>
              We pull numbers directly from the platform of record rather than a proprietary dashboard that re-processes the data. Search visibility comes from Google Search Console, because it reflects what Google itself recorded, not a third-party crawler's estimate. Traffic and behaviour come from GA4 when a client has it configured correctly (a surprising number of sites we inherit have broken or duplicate GA4 setups, which is itself often the first fix). Keyword and competitive tracking comes from Semrush, run against the same tracked keyword set for the life of the engagement so month-to-month comparisons are apples to apples rather than a shifting basket of terms.
            </p>
            <p>
              We deliberately avoid blending channels. If a client also runs paid search or paid social, we do not fold that traffic or those conversions into the organic numbers reported here — mixing the two flatters the report and makes it impossible to tell what organic search is actually contributing on its own.
            </p>
          </div>

          <h3 className="mt-10 font-display text-2xl">Which metrics we report, and why</h3>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/85">
            <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden /><span><strong className="text-foreground">Organic clicks and impressions (GSC)</strong> — the most direct read on visibility and demand capture, and the metric least distorted by third-party estimation.</span></li>
            <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden /><span><strong className="text-foreground">Ranking keyword count and position distribution (Semrush)</strong> — a leading indicator; rankings usually move before traffic catches up, so this shows momentum ahead of the traffic charts.</span></li>
            <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden /><span><strong className="text-foreground">Referring domains</strong> — a proxy for authority growth from digital PR and outreach work, tracked as unique domains rather than raw link count, since link count alone is easy to inflate with low-quality sources.</span></li>
            <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden /><span><strong className="text-foreground">Qualified leads or conversions (GA4 / call tracking)</strong> — the metric that actually matters to the business, even though it is the hardest to attribute cleanly to search alone.</span></li>
            <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden /><span><strong className="text-foreground">AI-search citations</strong> — whether a client's content is being surfaced in AI Overviews, ChatGPT, or Perplexity answers for target queries, tracked because this channel is growing and behaves differently to classic organic rankings.</span></li>
          </ul>

          <h3 className="mt-10 font-display text-2xl">Attribution caveats we take seriously</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            No agency can honestly claim sole credit for every point of organic growth during an engagement, and we do not try to. Google rolls out core updates several times a year, any of which can move rankings independently of anything we did. Competitors launch their own content and link campaigns in the same window. Seasonality changes search volume for reasons that have nothing to do with optimisation work. A client's own product changes, pricing changes, or brand mentions can shift branded search demand and inflate organic numbers that have little to do with technical or content work.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            We manage this by documenting known external events — a core update, a site migration, a paid campaign pause — in the same reporting period they happen, so a client reviewing six months of history later can see what coincided with what. We also separate branded from non-branded search performance wherever possible, since non-branded growth is a cleaner signal of genuine visibility gains than branded search, which mostly reflects existing brand awareness.
          </p>

          <h3 className="mt-10 font-display text-2xl">Measuring AI-search citations</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            AI-search visibility is measured differently to classic rankings, and the tooling is younger and less standardised. We track a defined list of target queries relevant to each client and record whether their content is cited, quoted, or linked in AI Overviews, ChatGPT, and Perplexity responses, using Semrush's AI Search tracking supplemented with manual spot checks on high-priority queries. Because these answers can vary between sessions, models, and even successive queries on the same day, we treat a single snapshot as a data point rather than a fixed fact, and look at the trend across repeated checks over weeks rather than any one capture.
          </p>

          <h3 className="mt-10 font-display text-2xl">Reporting cadence</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            Standard reporting runs monthly: a written summary of what changed, what we did, and what we recommend next, alongside the live dashboard data. During periods of heavier technical work — a migration, a large-scale schema rollout, an information-architecture change — we typically move to a fortnightly check-in so issues surface quickly, then return to the monthly cadence once things settle. We would rather a client hear about a problem from us in week two than discover it themselves in the month-end report.
          </p>

          <h3 className="mt-10 font-display text-2xl">How to read any agency's case study critically</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            This applies to ours as much as anyone else's. Ask what the baseline was before you accept a percentage gain — a jump from two clicks to twenty is technically a 900% increase and means almost nothing. Ask what date range the numbers cover, and whether that range was cherry-picked to include a seasonal peak. Ask whether the traffic reported is branded, non-branded, or blended, since branded search often grows for reasons unrelated to SEO work. Ask what tool the screenshot came from and whether it can be independently verified. And ask what didn't work — a case study that reports only wins, with no mention of anything that needed adjusting along the way, is telling you less than it appears to.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
